import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Shield, FileText, AlertCircle, BarChart3, Users, ClipboardCheck, GraduationCap, Briefcase, Plus, AlertTriangle, CheckCircle } from 'lucide-react';
import api from '../lib/api';
import { cpdAPI } from '../lib/cpdAPI';
import { toast } from 'sonner';
import { formatAUDate } from '../lib/dateUtils';

// `section` renders a single legacy tab body (embedded in the merged QMS page).
export default function QMS({ section }) {
  const [dashboard, setDashboard] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [cpdRecords, setCpdRecords] = useState([]);
  const [competency, setCompetency] = useState(null);
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCPDDialog, setShowCPDDialog] = useState(false);
  const [selectedCPDYear, setSelectedCPDYear] = useState('2024-25');
  
  const currentUserId = localStorage.getItem('user_id') || 'current_user';
  
  const [cpdFormData, setCpdFormData] = useState({
    cpd_year: '2024-25',
    activity_date: new Date().toISOString().split('T')[0],
    activity_title: '',
    provider: '',
    category: 'tax_law',
    hours: ''
  });

  useEffect(() => {
    loadQMSData();
  }, []);

  const loadQMSData = async () => {
    try {
      const [dashRes, policiesRes, incidentsRes, cpdRes, competencyRes, engagementsRes] = await Promise.all([
        api.get('/api/qms/dashboard'),
        api.get('/api/qms/policies'),
        api.get('/api/qms/incidents'),
        cpdAPI.listRecords({ staff_id: currentUserId, cpd_year: selectedCPDYear }),
        cpdAPI.getCompetency(currentUserId),
        cpdAPI.listEngagements()
      ]);
      
      setDashboard(dashRes.data);
      setPolicies(policiesRes.data);
      setIncidents(incidentsRes.data);
      setCpdRecords(cpdRes.data || []);
      setCompetency(competencyRes.data);
      setEngagements(engagementsRes.data || []);
    } catch (error) {
      toast.error('Failed to load QMS data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse h-64 bg-muted rounded"></div>
      </div>
    );
  }

  const severityColors = {
    low: 'bg-muted text-foreground/90',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-orange-100 text-orange-800',
    critical: 'bg-red-100 text-red-800'
  };

  const statusColors = {
    open: 'bg-red-100 text-red-800',
    investigating: 'bg-yellow-100 text-yellow-800',
    resolved: 'bg-blue-100 text-blue-800',
    closed: 'bg-green-100 text-green-800'
  };

  return (
    <div className={section ? '' : 'p-6 space-y-8'} data-testid={section ? `qms-section-${section}` : undefined}>
      {!section && <>
      <div>
        <h1 className="text-3xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
          Quality Management System (QMS)
        </h1>
        <p className="text-muted-foreground mt-1">APES 320 compliant quality controls and monitoring</p>
      </div>

      {/* QMS Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Published Policies</p>
                <p className="text-3xl font-bold text-foreground mt-2">{dashboard?.total_policies || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Open Incidents</p>
                <p className="text-3xl font-bold text-foreground mt-2">{dashboard?.open_incidents || 0}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Shield className="w-10 h-10 text-primary" />
              <div>
                <p className="font-medium text-foreground">QMS Status</p>
                <p className="text-sm text-muted-foreground">{dashboard?.status}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      </>}

      {/* Tabs */}
      <Tabs {...(section ? { value: section } : { defaultValue: 'dashboard' })} className="w-full">
        {!section && <TabsList>
          <TabsTrigger value="dashboard">
            <Shield className="w-4 h-4 mr-2" />
            QMS Dashboard
          </TabsTrigger>
          <TabsTrigger value="cpd">
            <GraduationCap className="w-4 h-4 mr-2" />
            CPD Tracking
          </TabsTrigger>
          <TabsTrigger value="engagements">
            <Briefcase className="w-4 h-4 mr-2" />
            Engagements
          </TabsTrigger>
          <TabsTrigger value="policies">
            <FileText className="w-4 h-4 mr-2" />
            Policies ({policies.length})
          </TabsTrigger>
          <TabsTrigger value="ai-policy">
            <Shield className="w-4 h-4 mr-2" />
            AI Policy
          </TabsTrigger>
          <TabsTrigger value="incidents">
            <AlertCircle className="w-4 h-4 mr-2" />
            Incidents ({incidents.length})
          </TabsTrigger>
        </TabsList>}

        <TabsContent value="dashboard">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <CardContent className="pt-6 text-center">
                  <GraduationCap className="w-12 h-12 mx-auto mb-3 text-primary" />
                  <p className="text-sm text-muted-foreground">CPD Compliance</p>
                  <p className="text-3xl font-bold mt-2">{dashboard?.cpd_compliance_percentage || 0}%</p>
                  <p className="text-xs text-muted-foreground mt-1">{dashboard?.compliant_staff || 0} of {dashboard?.total_staff || 0} staff compliant or on track</p>
                  <a href="/compliance/cpd" className="text-xs text-primary underline">Open CPD Register</a>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <AlertCircle className="w-12 h-12 mx-auto mb-3 text-amber-600" />
                  <p className="text-sm text-muted-foreground">Open Actions</p>
                  <p className="text-3xl font-bold mt-2">{dashboard?.open_remediation_actions || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <Briefcase className="w-12 h-12 mx-auto mb-3 text-red-600" />
                  <p className="text-sm text-muted-foreground">High-Risk Engagements</p>
                  <p className="text-3xl font-bold mt-2">{dashboard?.high_risk_engagements || 0}</p>
                </CardContent>
              </Card>
            </div>
            
            <Card className="border-l-4 border-l-[#0055FF]">
              <CardHeader>
                <CardTitle>Firm Risk Assessment</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold">Overall Firm Risk Level:</span>
                  <Badge className={
                    dashboard?.firm_risk_level === 'low' ? 'bg-green-100 text-green-800 text-lg px-4 py-2' :
                    dashboard?.firm_risk_level === 'medium' ? 'bg-amber-100 text-amber-800 text-lg px-4 py-2' :
                    'bg-red-100 text-red-800 text-lg px-4 py-2'
                  }>
                    {dashboard?.firm_risk_level?.toUpperCase() || 'UNKNOWN'}
                  </Badge>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-6 text-sm">
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-green-700 font-semibold">{dashboard?.compliant_staff || 0} Compliant</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <p className="text-amber-700 font-semibold">{dashboard?.at_risk_staff || 0} At Risk</p>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg">
                    <p className="text-red-700 font-semibold">{dashboard?.non_compliant_staff || 0} Non-Compliant</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="cpd">
          <div className="space-y-6">
            <Card data-testid="qms-cpd-register-link"><CardContent className="py-4 flex items-center justify-between gap-4">
              <p className="text-sm">CPD is now managed in the <b>CPD Register</b> (Compliance). Records below are the legacy, read-only view.</p>
              <Button onClick={() => { window.location.href = '/compliance/cpd'; }}>Open CPD Register</Button>
            </CardContent></Card>
            {competency && (
              <Card className="border-l-4 border-l-[#0055FF]">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center">
                      {competency.compliance_status === 'compliant' ? <CheckCircle className="w-6 h-6 text-green-600" /> :
                       competency.compliance_status === 'at_risk' ? <AlertTriangle className="w-6 h-6 text-amber-600" /> :
                       <AlertCircle className="w-6 h-6 text-red-600" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-xl font-semibold" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>TPB Compliance Status</h3>
                        <Badge className={
                          competency.compliance_status === 'compliant' ? 'bg-green-100 text-green-800' :
                          competency.compliance_status === 'at_risk' ? 'bg-amber-100 text-amber-800' :
                          'bg-red-100 text-red-800'
                        }>
                          {competency.compliance_status.replace('_', ' ').toUpperCase()}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-6">
                        <div>
                          <p className="text-sm text-muted-foreground mb-2">Total Hours</p>
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span>{competency.completed_hours_total} / {competency.required_hours_total}</span>
                              <span className="font-semibold">{Math.round((competency.completed_hours_total / competency.required_hours_total) * 100)}%</span>
                            </div>
                            <Progress value={(competency.completed_hours_total / competency.required_hours_total) * 100} className="h-2" />
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground mb-2">Tax Law Hours</p>
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span>{competency.completed_hours_tax} / {competency.required_hours_tax}</span>
                              <span className="font-semibold">{Math.round((competency.completed_hours_tax / competency.required_hours_tax) * 100)}%</span>
                            </div>
                            <Progress value={(competency.completed_hours_tax / competency.required_hours_tax) * 100} className="h-2" />
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground mb-2">Ethics Hours</p>
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span>{competency.completed_hours_ethics} / {competency.required_hours_ethics}</span>
                              <span className="font-semibold">{Math.round((competency.completed_hours_ethics / competency.required_hours_ethics) * 100)}%</span>
                            </div>
                            <Progress value={(competency.completed_hours_ethics / competency.required_hours_ethics) * 100} className="h-2" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>CPD Activities</CardTitle>
                  <Button className="bg-primary hover:bg-primary/90" onClick={() => setShowCPDDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Log Activity
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {cpdRecords.map((record) => (
                    <div key={record.id} className="p-4 border rounded-lg">
                      <div className="flex justify-between">
                        <div>
                          <h4 className="font-semibold">{record.activity_title}</h4>
                          <p className="text-sm text-muted-foreground">{record.provider}</p>
                          <Badge variant="outline" className="mt-2 text-xs">{record.category.replace('_', ' ')}</Badge>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-primary">{record.hours}h</p>
                          <p className="text-xs text-muted-foreground">{formatAUDate(record.activity_date)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="engagements">
          <Card>
            <CardHeader>
              <CardTitle>Engagement Compliance & Risk Scoring</CardTitle>
            </CardHeader>
            <CardContent>
              {engagements.length === 0 ? (
                <div className="text-center py-12">
                  <Briefcase className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-muted-foreground">No engagements found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Client</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Assigned Staff</TableHead>
                      <TableHead>Complexity</TableHead>
                      <TableHead>Risk Score</TableHead>
                      <TableHead>Review Required</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {engagements.map((eng) => (
                      <TableRow key={eng.id}>
                        <TableCell className="font-medium">{eng.client_name}</TableCell>
                        <TableCell>{eng.engagement_type.replace('_', ' ')}</TableCell>
                        <TableCell>{eng.staff_name}</TableCell>
                        <TableCell><Badge variant="outline">{eng.complexity}</Badge></TableCell>
                        <TableCell>
                          <Badge className={
                            eng.risk_score === 'low' ? 'bg-green-100 text-green-800' :
                            eng.risk_score === 'medium' ? 'bg-amber-100 text-amber-800' :
                            'bg-red-100 text-red-800'
                          }>
                            {eng.risk_score.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {eng.requires_review ? (
                            <span className="text-red-600 text-sm flex items-center gap-1">
                              <AlertTriangle className="w-4 h-4" /> Yes
                            </span>
                          ) : (
                            <span className="text-muted-foreground/70">No</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={eng.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}>
                            {eng.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="policies">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Policies & Procedures</CardTitle>
                <Button className="bg-primary hover:bg-primary/90">Create Policy</Button>
              </div>
            </CardHeader>
            <CardContent>
              {policies.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                  <p>No policies yet</p>
                  <p className="text-sm mt-1">Create your first policy to begin QMS documentation</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {policies.map((policy) => (
                    <div key={policy.id} className="p-4 border rounded-lg hover:bg-muted/50">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-foreground">{policy.title}</h4>
                          <p className="text-sm text-muted-foreground">Version {policy.version} • Owner: {policy.owner_id}</p>
                        </div>
                        <Badge variant="outline">{policy.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ai-policy">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>AI Use Policy - Tax Agent Services Platform</CardTitle>
                <Badge className="bg-green-100 text-green-800">TPB Compliant</Badge>
              </div>
            </CardHeader>
            <CardContent className="prose prose-slate max-w-none">
              <div className="space-y-6">
                {/* 1. Purpose */}
                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-2" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
                    1. Purpose
                  </h3>
                  <p className="text-foreground/90">
                    This policy governs the use of Artificial Intelligence (AI) within the platform to ensure compliance with:
                  </p>
                  <ul className="list-disc pl-6 text-foreground/90 space-y-1">
                    <li>Tax Agent Services Act 2009 (TASA)</li>
                    <li>Code of Professional Conduct</li>
                    <li>TPB AI guidance (Exposure Draft TPB(I) D62/2026)</li>
                  </ul>
                </section>

                {/* 2. Scope */}
                <section className="pt-4 border-t">
                  <h3 className="text-lg font-semibold text-foreground mb-2" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
                    2. Scope
                  </h3>
                  <p className="text-foreground/90 mb-2">Applies to:</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="font-semibold text-foreground mb-2">All AI systems used in:</p>
                      <ul className="list-disc pl-6 text-foreground/90 space-y-1">
                        <li>Tax return preparation</li>
                        <li>Client data processing</li>
                        <li>Advisory outputs</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground mb-2">All users:</p>
                      <ul className="list-disc pl-6 text-foreground/90 space-y-1">
                        <li>Staff</li>
                        <li>Clients (via portal)</li>
                        <li>Automated agents</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* 3. Principles */}
                <section className="pt-4 border-t">
                  <h3 className="text-lg font-semibold text-foreground mb-3" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
                    3. Principles
                  </h3>
                  
                  <div className="space-y-4">
                    {/* 3.1 */}
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <h4 className="font-semibold text-red-900 mb-2">3.1 Human Oversight (MANDATORY)</h4>
                      <p className="text-sm text-red-800 mb-2">All AI-generated outputs must be:</p>
                      <ul className="list-disc pl-6 text-sm text-red-800 space-y-1">
                        <li>Reviewed by a registered tax practitioner</li>
                      </ul>
                      <p className="text-sm text-red-900 font-semibold mt-3 mb-1">AI cannot:</p>
                      <ul className="list-disc pl-6 text-sm text-red-800 space-y-1">
                        <li>Lodge returns independently</li>
                        <li>Provide final advice without review</li>
                      </ul>
                    </div>

                    {/* 3.2 */}
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <h4 className="font-semibold text-blue-900 mb-2">3.2 Professional Responsibility</h4>
                      <p className="text-sm text-blue-800 mb-2">The firm retains full responsibility for:</p>
                      <ul className="list-disc pl-6 text-sm text-blue-800 space-y-1">
                        <li>Accuracy of returns</li>
                        <li>Advice provided</li>
                      </ul>
                      <p className="text-sm text-blue-800 mt-2">
                        <strong>AI outputs are treated as draft assistance only</strong>
                      </p>
                    </div>

                    {/* 3.3 */}
                    <div className="p-4 bg-muted/50 border border-border rounded-lg">
                      <h4 className="font-semibold text-foreground mb-2">3.3 Competency Controls</h4>
                      <p className="text-sm text-foreground/90 mb-2">AI must:</p>
                      <ul className="list-disc pl-6 text-sm text-foreground/90 space-y-1">
                        <li>Be trained on relevant tax frameworks</li>
                        <li>Be restricted to defined use cases</li>
                      </ul>
                      <p className="text-sm text-foreground/90 font-semibold mt-3 mb-1">Mandatory validation layers:</p>
                      <ul className="list-disc pl-6 text-sm text-foreground/90 space-y-1">
                        <li>Rule-based checks</li>
                        <li>Exception flags</li>
                      </ul>
                    </div>

                    {/* 3.4 */}
                    <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                      <h4 className="font-semibold text-purple-900 mb-2">3.4 Confidentiality & Data Security</h4>
                      <p className="text-sm text-purple-800 mb-2">Client data must:</p>
                      <ul className="list-disc pl-6 text-sm text-purple-800 space-y-1">
                        <li>Not be shared with external AI systems without consent</li>
                        <li>Be encrypted in storage and transit</li>
                        <li><strong>NEVER be used for AI model training</strong></li>
                        <li>Not be incorporated into AI data models accessible to others</li>
                      </ul>
                      <p className="text-sm text-purple-900 font-semibold mt-3 mb-1">Automatic Protection Measures:</p>
                      <ul className="list-disc pl-6 text-sm text-purple-800 space-y-1">
                        <li>System designed to reduce risk of exposing client-sensitive data</li>
                        <li>Client inputs NOT used for training AI models</li>
                        <li>Reduces "shadow AI use" by providing compliant firm alternative</li>
                        <li>PII protection mechanisms in place</li>
                      </ul>
                      <p className="text-sm text-purple-900 font-semibold mt-3 mb-1">No use of:</p>
                      <ul className="list-disc pl-6 text-sm text-purple-800 space-y-1">
                        <li>Public AI tools without data isolation</li>
                        <li>Free AI platforms that train on user data</li>
                      </ul>
                      <div className="mt-3 p-3 bg-purple-100 rounded border border-purple-300">
                        <p className="text-xs text-purple-900">
                          <strong>Key Principle:</strong> By providing SavvyWise-level AI capabilities within this platform, 
                          we reduce incentive for staff to use unauthorized AI tools, while automatically protecting client data 
                          from being incorporated into external AI training models.
                        </p>
                      </div>
                    </div>

                    {/* 3.5 */}
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <h4 className="font-semibold text-amber-900 mb-2">3.5 Transparency</h4>
                      <p className="text-sm text-amber-800 mb-2">Clients must be informed:</p>
                      <ul className="list-disc pl-6 text-sm text-amber-800 space-y-1">
                        <li>AI is used in processing their data</li>
                      </ul>
                      <p className="text-sm text-amber-800 mt-2">Disclosure included in:</p>
                      <ul className="list-disc pl-6 text-sm text-amber-800 space-y-1">
                        <li>Engagement letters</li>
                        <li>Platform terms</li>
                      </ul>
                    </div>

                    {/* 3.6 */}
                    <div className="p-4 bg-accent border border-primary/20 rounded-lg">
                      <h4 className="font-semibold text-accent-foreground mb-2">3.6 Reasonable Care & Verification</h4>
                      <p className="text-sm text-accent-foreground mb-2">AI outputs must be cross-checked against:</p>
                      <ul className="list-disc pl-6 text-sm text-accent-foreground space-y-1">
                        <li>ATO data</li>
                        <li>Client documents</li>
                      </ul>
                      <p className="text-sm text-accent-foreground font-semibold mt-3 mb-1">High-risk areas flagged:</p>
                      <ul className="list-disc pl-6 text-sm text-accent-foreground space-y-1">
                        <li>CGT (Capital Gains Tax)</li>
                        <li>Division 7A</li>
                        <li>Trust distributions</li>
                      </ul>
                    </div>

                    {/* 3.7 */}
                    <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                      <h4 className="font-semibold text-indigo-900 mb-2">3.7 Audit Trail & Record Keeping</h4>
                      <p className="text-sm text-indigo-800 mb-2">System must log:</p>
                      <ul className="list-disc pl-6 text-sm text-indigo-800 space-y-1">
                        <li>Inputs (client data)</li>
                        <li>AI outputs</li>
                        <li>Human review actions</li>
                        <li>Final submission decision</li>
                      </ul>
                    </div>

                    {/* 3.8 */}
                    <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                      <h4 className="font-semibold text-orange-900 mb-2">3.8 Bias & Error Management</h4>
                      <p className="text-sm text-orange-800 mb-2">AI must:</p>
                      <ul className="list-disc pl-6 text-sm text-orange-800 space-y-1">
                        <li>Be periodically tested</li>
                        <li>Be monitored for incorrect outputs</li>
                      </ul>
                      <p className="text-sm text-orange-800 mt-2">Errors must trigger:</p>
                      <ul className="list-disc pl-6 text-sm text-orange-800 space-y-1">
                        <li>Incident review</li>
                        <li>Model adjustment</li>
                      </ul>
                    </div>

                    {/* 3.9 */}
                    <div className="p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
                      <h4 className="font-semibold text-cyan-900 mb-2">3.9 Supervision Framework</h4>
                      <p className="text-sm text-cyan-800 mb-2">AI operates under:</p>
                      <ul className="list-disc pl-6 text-sm text-cyan-800 space-y-1">
                        <li>Defined workflows</li>
                        <li>Approval hierarchies</li>
                      </ul>
                      <p className="text-sm text-cyan-800 mt-2">
                        <strong>Junior staff + AI = still requires senior review</strong>
                      </p>
                    </div>

                    {/* 3.10 */}
                    <div className="p-4 bg-red-50 border-2 border-red-300 rounded-lg">
                      <h4 className="font-semibold text-red-900 mb-2">3.10 Prohibited Use Cases</h4>
                      <p className="text-sm text-red-900 font-semibold mb-2">AI must NOT:</p>
                      <ul className="list-disc pl-6 text-sm text-red-800 space-y-1">
                        <li>Provide tax advice without human review</li>
                        <li>Auto-lodge returns</li>
                        <li>Infer missing data without disclosure</li>
                        <li>Override client-provided information</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* 4. Governance */}
                <section className="pt-4 border-t">
                  <h3 className="text-lg font-semibold text-foreground mb-3" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
                    4. Governance
                  </h3>
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-900 mb-2"><strong>Assign:</strong></p>
                    <ul className="list-disc pl-6 text-sm text-green-800 space-y-1">
                      <li>AI Compliance Officer</li>
                    </ul>
                    <p className="text-sm text-green-900 font-semibold mt-3 mb-1">Quarterly:</p>
                    <ul className="list-disc pl-6 text-sm text-green-800 space-y-1">
                      <li>AI audit review</li>
                    </ul>
                    <p className="text-sm text-green-900 font-semibold mt-3 mb-1">Annual:</p>
                    <ul className="list-disc pl-6 text-sm text-green-800 space-y-1">
                      <li>Policy update aligned to TPB</li>
                    </ul>
                  </div>
                </section>

                {/* Implementation Status */}
                <section className="pt-4 border-t">
                  <h3 className="text-lg font-semibold text-foreground mb-3" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
                    Platform Implementation Status
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-3 border rounded-lg">
                      <p className="font-semibold text-green-700 mb-2 flex items-center gap-2">
                        <ClipboardCheck className="w-4 h-4" /> Implemented Controls
                      </p>
                      <ul className="text-sm text-foreground/90 space-y-1">
                        <li>✓ Mandatory human review (Tax Return module)</li>
                        <li>✓ No auto-lodge capability</li>
                        <li>✓ Crypto/foreign income flags mandatory review</li>
                        <li>✓ Complete audit trails in all modules</li>
                        <li>✓ Confidence scoring on AI extractions</li>
                        <li>✓ Review workflows enforced</li>
                        <li>✓ TFN masking in UI</li>
                        <li>✓ Document retention controls</li>
                      </ul>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <p className="font-semibold text-amber-700 mb-2">AI Features in Platform</p>
                      <ul className="text-sm text-foreground/90 space-y-1">
                        <li>• Tax document extraction (OpenAI GPT-4)</li>
                        <li>• Transaction classification (rule-based)</li>
                        <li>• Risk assessment scoring (rule-based)</li>
                        <li>• Reconciliation detection (rule-based)</li>
                        <li>• All require human approval</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Footer */}
                <div className="pt-6 mt-6 border-t">
                  <p className="text-xs text-muted-foreground text-center">
                    Last Updated: {new Date().toLocaleDateString('en-AU')} | Version 1.0 | 
                    Compliant with Tax Practitioners Board Guidance
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="incidents">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Incident & Complaints Register</CardTitle>
                <Button className="bg-primary hover:bg-primary/90">Report Incident</Button>
              </div>
            </CardHeader>
            <CardContent>
              {incidents.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <AlertCircle className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                  <p>No incidents reported</p>
                  <p className="text-sm mt-1">Incidents, complaints, and near-misses will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {incidents.map((incident) => (
                    <div key={incident.id} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex gap-2">
                          <Badge className={severityColors[incident.severity]}>
                            {incident.severity.toUpperCase()}
                          </Badge>
                          <Badge className={statusColors[incident.status]}>
                            {incident.status}
                          </Badge>
                        </div>
                        <span className="text-xs text-muted-foreground">{incident.type}</span>
                      </div>
                      <p className="text-sm text-foreground">{incident.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* CPD Log Dialog */}
      <Dialog open={showCPDDialog} onOpenChange={setShowCPDDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log CPD Activity</DialogTitle>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            try {
              await cpdAPI.createRecord({
                staff_id: currentUserId,
                ...cpdFormData,
                hours: parseFloat(cpdFormData.hours)
              });
              toast.success('CPD logged');
              setShowCPDDialog(false);
              loadQMSData();
            } catch (error) {
              toast.error('Failed to log CPD');
            }
          }} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Activity Date *</Label>
                <Input type="date" value={cpdFormData.activity_date} onChange={(e) => setCpdFormData({...cpdFormData, activity_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Hours *</Label>
                <Input type="number" step="0.5" value={cpdFormData.hours} onChange={(e) => setCpdFormData({...cpdFormData, hours: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Activity Title *</Label>
              <Input value={cpdFormData.activity_title} onChange={(e) => setCpdFormData({...cpdFormData, activity_title: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Provider</Label>
              <Input value={cpdFormData.provider} onChange={(e) => setCpdFormData({...cpdFormData, provider: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={cpdFormData.category} onValueChange={(v) => setCpdFormData({...cpdFormData, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tax_law">Tax Law</SelectItem>
                  <SelectItem value="ethics">Ethics</SelectItem>
                  <SelectItem value="practice_management">Practice Management</SelectItem>
                  <SelectItem value="technology">Technology</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setShowCPDDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90">Log Activity</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
